import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { runSandboxPreviewApi } from '../../api/sandbox.api';
import { loadAppState } from '../../utils/storage';
import { buildPreviewFiles, createPreviewSourceKey } from '../../utils/previewFiles';
import { SANDBOX_PREVIEW_TIMEOUT_MS } from '../../utils/previewTiming';

const persistedState = loadAppState();
export const SANDBOX_PREVIEW_EXPECTED_DURATION_MS = SANDBOX_PREVIEW_TIMEOUT_MS;

const getPreviewEntry = (state, projectId) => state.byProjectId[projectId] || {};
const getSourceKeyFromArg = ({ files, previewHtml, language = 'en' }) => {
  const preparedFiles = buildPreviewFiles({ files, html: previewHtml, language });
  return preparedFiles ? createPreviewSourceKey({ files: preparedFiles, language }) : null;
};

export const runProjectSandbox = createAsyncThunk(
  'preview/runProjectSandbox',
  async ({ projectId }, { getState }) => {
    const existingEntry = getPreviewEntry(getState().preview, projectId);
    const response = await runSandboxPreviewApi({ projectId });

    return {
      projectId,
      ...response,
      files: existingEntry.files,
      sourceKey: existingEntry.sourceKey
    };
  }
);

export const runSandboxPreview = createAsyncThunk(
  'preview/runSandboxPreview',
  async ({ projectId, files, previewHtml, language = 'en', force = false }, { getState, rejectWithValue }) => {
    const previewState = getState().preview;
    const existingEntry = getPreviewEntry(previewState, projectId);
    const preparedFiles = buildPreviewFiles({ files, html: previewHtml, language });

    if (!preparedFiles) {
      return rejectWithValue('No preview files available.');
    }

    const sourceKey = createPreviewSourceKey({ files: preparedFiles, language });

    if (!force && existingEntry.sourceKey === sourceKey && existingEntry.previewUrl) {
      return {
        projectId,
        ...existingEntry,
        files: preparedFiles,
        sourceKey
      };
    }

    const response = await runSandboxPreviewApi({ projectId });

    return {
      projectId,
      ...response,
      files: preparedFiles,
      sourceKey
    };
  },
  {
    condition: (arg, { getState }) => {
      const previewState = getState().preview;
      const existingEntry = getPreviewEntry(previewState, arg.projectId);
      const sourceKey = getSourceKeyFromArg(arg);

      return !(sourceKey && existingEntry.status === 'loading' && existingEntry.sourceKey === sourceKey);
    }
  }
);

const previewSlice = createSlice({
  name: 'preview',
  initialState: {
    viewMode: persistedState.preview?.viewMode || 'desktop',
    byProjectId: persistedState.preview?.byProjectId || {}
  },
  reducers: {
    setViewMode: (state, action) => {
      state.viewMode = action.payload;
    },
    refreshPreviewIframe: (state, action) => {
      const { projectId } = action.payload;
      const entry = getPreviewEntry(state, projectId);

      state.byProjectId[projectId] = {
        ...entry,
        iframeRefreshNonce: (entry.iframeRefreshNonce || 0) + 1
      };
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(runSandboxPreview.pending, (state, action) => {
        const { projectId } = action.meta.arg;
        state.byProjectId[projectId] = {
          ...getPreviewEntry(state, projectId),
          sourceKey: getSourceKeyFromArg(action.meta.arg) || getPreviewEntry(state, projectId).sourceKey,
          status: 'loading',
          loadingStartedAt: Date.now(),
          loadingExpectedDurationMs: SANDBOX_PREVIEW_EXPECTED_DURATION_MS,
          error: null
        };
      })
      .addCase(runSandboxPreview.fulfilled, (state, action) => {
        const { projectId, files, sourceKey, ...previewData } = action.payload;
        state.byProjectId[projectId] = {
          ...getPreviewEntry(state, projectId),
          ...previewData,
          files,
          sourceKey,
          status: 'succeeded',
          loadingStartedAt: null,
          loadingExpectedDurationMs: null,
          error: null
        };
      })
      .addCase(runSandboxPreview.rejected, (state, action) => {
        const { projectId } = action.meta.arg;
        state.byProjectId[projectId] = {
          ...getPreviewEntry(state, projectId),
          sourceKey: getSourceKeyFromArg(action.meta.arg) || getPreviewEntry(state, projectId).sourceKey,
          status: 'failed',
          loadingStartedAt: null,
          loadingExpectedDurationMs: null,
          error: action.payload || action.error.message || 'Sandbox preview failed.'
        };
      })
      .addCase(runProjectSandbox.pending, (state, action) => {
        const { projectId } = action.meta.arg;
        state.byProjectId[projectId] = {
          ...getPreviewEntry(state, projectId),
          status: 'loading',
          loadingStartedAt: Date.now(),
          loadingExpectedDurationMs: SANDBOX_PREVIEW_EXPECTED_DURATION_MS,
          error: null
        };
      })
      .addCase(runProjectSandbox.fulfilled, (state, action) => {
        const { projectId, files, sourceKey, ...previewData } = action.payload;
        state.byProjectId[projectId] = {
          ...getPreviewEntry(state, projectId),
          ...previewData,
          files,
          sourceKey,
          status: 'succeeded',
          loadingStartedAt: null,
          loadingExpectedDurationMs: null,
          error: null
        };
      })
      .addCase(runProjectSandbox.rejected, (state, action) => {
        const { projectId } = action.meta.arg;
        state.byProjectId[projectId] = {
          ...getPreviewEntry(state, projectId),
          status: 'failed',
          loadingStartedAt: null,
          loadingExpectedDurationMs: null,
          error: action.payload || action.error.message || 'Sandbox preview failed.'
        };
      });
  }
});

export const { setViewMode, refreshPreviewIframe } = previewSlice.actions;
export default previewSlice.reducer;
